import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { CaretLeft, CaretRight, CaretDown, CaretUp, House, Folders, Archive, ArrowClockwise, Trash, DownloadSimple, UploadSimple, BookOpen } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { PWAInstallButton } from '@/components/PWAInstallButton'
import { useApp } from '@/contexts/AppContext'
import { getOpenQuestionCount } from '@/contexts/AppContext'
import { cn } from '@/lib/utils'
import { storage } from '@/lib/storage'
import { useDarkMode } from '@/hooks/use-dark-mode'
import { useDialog } from '@/contexts/DialogContext'
import type { ChangeEvent } from 'react'
import type { AppState } from '@/types'

interface SidebarProps {
  className?: string
}

export function Sidebar({ className }: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isEntitiesOpen, setIsEntitiesOpen] = useState(false)
  const [isArchivioOpen, setIsArchivioOpen] = useState(false)
  const { state, dispatch } = useApp()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const importInputRef = useRef<HTMLInputElement | null>(null)
  const { isDarkMode, toggleDarkMode } = useDarkMode()
  const dialog = useDialog()
  const verboseMode = state.settings?.verboseMode ?? true

  const nonArchived = Object.values(state.entities)
    .filter(entity => !entity.isArchived)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
  const archived = Object.values(state.entities)
    .filter(entity => entity.isArchived)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang)
  }

  const handleShortcutsToggle = (enabled: boolean | string) => {
    // Some checkbox libraries can pass 'indeterminate' or string; normalize to boolean
    const next = typeof enabled === 'boolean' ? enabled : enabled === 'true'
    dispatch({ type: 'TOGGLE_SHORTCUTS', payload: { enabled: next } })
  }

  // Track whether compact (alternate) mode is active on the entity thread list page.
  const [isEntityCompactMode, setIsEntityCompactMode] = useState(false)
  // Track whether Ctrl+S/D are available due to create/edit modes
  const [entitiesCreating, setEntitiesCreating] = useState(false)
  const [entityCreating, setEntityCreating] = useState(false)
  const [entityEditingCompact, setEntityEditingCompact] = useState(false)
  const [entitiesTitleEditing, setEntitiesTitleEditing] = useState(false)

  useEffect(() => {
    const handler = (e: Event) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const detail = (e as CustomEvent<any>).detail
      if (detail && typeof detail.compact === 'boolean') {
        setIsEntityCompactMode(detail.compact)
      }
    }
    window.addEventListener('entity-compact-mode-change', handler as EventListener)
    return () => window.removeEventListener('entity-compact-mode-change', handler as EventListener)
  }, [])

  // Listen for creation/editing events to surface Ctrl+S/D across pages
  useEffect(() => {
    const onEntitiesCreate = (e: Event) => {
      const detail = (e as CustomEvent<{ creating: boolean }>).detail
      setEntitiesCreating(!!detail?.creating)
    }
    const onEntityCreate = (e: Event) => {
      const detail = (e as CustomEvent<{ creating: boolean }>).detail
      setEntityCreating(!!detail?.creating)
    }
    const onEntityCompactEditing = (e: Event) => {
      const detail = (e as CustomEvent<{ editing: boolean }>).detail
      setEntityEditingCompact(!!detail?.editing)
    }
    window.addEventListener('entities-create-mode-change', onEntitiesCreate as EventListener)
    window.addEventListener('entity-create-mode-change', onEntityCreate as EventListener)
    window.addEventListener('entity-compact-editing-change', onEntityCompactEditing as EventListener)
    const onEntitiesTitleEdit = (e: Event) => {
      const detail = (e as CustomEvent<{ entityId: string; editing: boolean }>).detail
      setEntitiesTitleEditing(prev => {
        // Any true signal should turn on, and false keeps on until all false. Simplify to last event for now
        return !!detail?.editing
      })
    }
    window.addEventListener('entities-title-editing-change', onEntitiesTitleEdit as EventListener)
    return () => {
      window.removeEventListener('entities-create-mode-change', onEntitiesCreate as EventListener)
      window.removeEventListener('entity-create-mode-change', onEntityCreate as EventListener)
      window.removeEventListener('entity-compact-editing-change', onEntityCompactEditing as EventListener)
      window.removeEventListener('entities-title-editing-change', onEntitiesTitleEdit as EventListener)
    }
  }, [])

  // Detect current page type based on pathname
  const getPageType = () => {
    const path = location.pathname
    if (path === '/') return 'home'
    if (path === '/entities') return 'entities'
    if (path.startsWith('/entities/') && path.includes('/threads/')) return 'thread'
    if (path.startsWith('/entities/')) return 'entity'
    if (path === '/archivio') return 'archivio'
    if (path.startsWith('/archivio/') && path.includes('/threads/')) return 'thread-readonly'
    if (path.startsWith('/archivio/')) return 'entity-readonly'
    return 'unknown'
  }

  // Get shortcuts relevant to current page

  const getRelevantShortcuts = () => {
    const pageType = getPageType()
    const shortcuts: Array<{ key: string; description: string }> = []

    switch (pageType) {
      case 'home':
        // No page-specific shortcuts on Home
        break
      case 'entities':
        shortcuts.push(
          { key: 'A', description: t('shortcutCreateEntity') },
          { key: 'Z', description: t('shortcutImportEntity') }
        )
        // Always show save/cancel for pages that support them (create/edit)
        shortcuts.push({ key: 'Ctrl+S', description: t('shortcutSave') })
        shortcuts.push({ key: 'Ctrl+D', description: t('shortcutCancel') })
        break
      case 'archivio':
        // Archivio is readonly, no shortcuts available
        break
      case 'entity':
        shortcuts.push(
          { key: 'A', description: t('shortcutCreateThread') },
          { key: 'Z', description: t('shortcutImportThread') }
        )
        // Always show save/cancel for pages that support them (create/edit or compact save-all)
        shortcuts.push({ key: 'Ctrl+S', description: t('shortcutSave') })
        shortcuts.push({ key: 'Ctrl+D', description: t('shortcutCancel') })
        break
      case 'thread':
        shortcuts.push(
          { key: 'A', description: t('shortcutCreateConclusiveAnswer') },
          { key: 'S', description: t('shortcutCreateInconclusiveAnswer') },
          { key: 'D', description: t('shortcutCreateFollowupQuestion') },
          { key: 'Ctrl+S', description: t('shortcutSaveAll') },
          { key: 'Ctrl+D', description: t('shortcutCancelAll') }
        )
        break
      case 'thread-readonly':
        // No shortcuts for readonly mode
        break
      case 'entity-readonly':
        // No shortcuts for readonly mode
        break
    }

    // Always add the global shortcuts
    shortcuts.push({ key: 'Ctrl+G', description: t('shortcutToggleShortcuts') || 'Toggle shortcuts' })

    return shortcuts
  }

  const relevantShortcuts = getRelevantShortcuts()
  const isHomeActive = location.pathname === '/'
  const isEntitiesActive = location.pathname.startsWith('/entities')
  const isArchivioActive = location.pathname.startsWith('/archivio')
  const isTutorialsActive = location.pathname.startsWith('/tutorials')

  const handleResetDemoData = async () => {
    const confirmed = await dialog.confirm({
      message: t('confirmResetData')
    })
    if (confirmed) {
      dispatch({ type: 'RESET_DATA' })
    }
  }

  const handleDeleteAllData = async () => {
    const confirmed = await dialog.confirm({
      message: t('confirmDeleteAllData'),
      type: 'warning'
    })
    if (!confirmed) {
      return
    }
    await storage.clearData()
    dispatch({ type: 'CLEAR_ALL_DATA' })
  }

  const handleExportData = async () => {
    try {
      const dump = await storage.exportDatabase()
      const blob = new Blob([JSON.stringify(dump, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      const ts = (dump.exportedAt || new Date().toISOString()).replace(/[:]/g, '-').split('.')[0]
      anchor.href = url
      anchor.download = `technical-analysis-q-db-export-${ts}.json`
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Export failed:', error)
    }
  }

  const handleImportClick = () => {
    importInputRef.current?.click()
  }

  const handleImportFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    const confirmed = await dialog.confirm({
      message: t('confirmImportData')
    })
    
    if (!confirmed) {
      event.target.value = ''
      return
    }

    try {
      const text = await file.text()
      const parsed = JSON.parse(text)
      if (!parsed || typeof parsed !== 'object' || !Array.isArray((parsed as any).stores)) {
        throw new Error('Invalid database dump format')
      }

      // Perform full DB import (drop + recreate schema and data)
      await storage.importDatabase(parsed)

      // Rehydrate in-memory state from the newly imported DB
      const appState = await storage.get<AppState>('app-state')
      if (appState) {
        dispatch({ type: 'SET_STATE', payload: appState as any })
      } else {
        // As a fallback, clear in-memory state
        dispatch({ type: 'CLEAR_ALL_DATA' })
      }
      await dialog.alert({ message: t('importDataSuccess') })
    } catch (error) {
      console.error('Import failed:', error)
      await dialog.error({ message: t('importDataError') })
    } finally {
      event.target.value = ''
    }
  }

  // Open the related submenu when navigating to a main route
  useEffect(() => {
    const p = location.pathname
    if (p.startsWith('/entities')) {
      setIsEntitiesOpen(true)
    }
    if (p.startsWith('/archivio')) {
      setIsArchivioOpen(true)
    }
  }, [location.pathname])

  return (
    <div className={cn(
      // Full-height sidebar with column layout; top and bottom remain fixed, middle can scroll
      "border-r bg-card flex flex-col h-screen min-h-0 transition-all duration-200",
      isCollapsed ? "w-12" : "w-64",
      className
    )}>
      {/* Header */}
      <div className="p-3 border-b flex items-center justify-between">
        {!isCollapsed && (
          <Link to="/" className="flex items-center gap-2 hover:underline">
            <House size={20} className="text-muted-foreground" />
            <span>{t('appTitle')}</span>
          </Link>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="h-6 w-6 p-0"
        >
          {isCollapsed ? <CaretRight size={12} /> : <CaretLeft size={12} />}
        </Button>
      </div>

      {/* Language Selector and PWA Install */}
      {!isCollapsed && (
        <div className="p-3 border-b space-y-2">
          <div className="flex items-center gap-2">
            <Label id="language-label" className="text-xs text-muted-foreground whitespace-nowrap">
              {t('language')}
            </Label>
            <div className="flex-1">
              <Select value={i18n.language} onValueChange={handleLanguageChange}>
                <SelectTrigger className="w-full h-8 text-xs" aria-labelledby="language-label">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="it">{t('italian')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* PWA Install Button */}
          <PWAInstallButton />
        </div>
      )}

      {/* Dark Mode Section */}
      {!isCollapsed && (
        <div className="p-3 border-b">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="dark-mode" className="text-xs font-medium text-muted-foreground cursor-pointer select-none">
              {t('darkMode')}
            </Label>
            <Checkbox
              id="dark-mode"
              checked={isDarkMode}
              onCheckedChange={(checked) => toggleDarkMode(Boolean(checked))}
            />
          </div>
        </div>
      )}

      {/* Verbose Mode Section */}
      {!isCollapsed && (
        <div className="p-3 border-b">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="verbose-mode" className="text-xs font-medium text-muted-foreground cursor-pointer select-none">
              {t('verboseMode')}
            </Label>
            <Checkbox
              id="verbose-mode"
              checked={state.settings?.verboseMode ?? true}
              onCheckedChange={(checked) => dispatch({ type: 'TOGGLE_VERBOSE_MODE', payload: { enabled: Boolean(checked) } })}
            />
          </div>
        </div>
      )}

      {/* Shortcuts Section (fixed height ~6 rows) */}
      {!isCollapsed && (
        <div className="p-3 border-b">
          <div className="space-y-3">
            <div className="flex items-center gap-2 justify-between">
              <Label className="text-xs font-medium text-muted-foreground">
                {t('shortcuts')}
              </Label>
              <div className="flex items-center gap-2">
                <Label htmlFor="shortcuts-enabled" className="text-xs text-muted-foreground cursor-pointer select-none">
                  {t('enableShortcuts')}
                </Label>
                <Checkbox 
                  id="shortcuts-enabled"
                  checked={state.settings?.shortcutsEnabled ?? true}
                  onCheckedChange={handleShortcutsToggle}
                />
              </div>
            </div>
            
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground space-y-0.5 overflow-y-auto min-h-[108px] max-h-[108px] pr-1">
                {relevantShortcuts.map((shortcut, index) => (
                  <div key={index} className="flex justify-between">
                    <span>{shortcut.key}</span>
                    <span>{shortcut.description}</span>
                  </div>
                ))}
                {relevantShortcuts.length === 0 && (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    {t('noShortcutsAvailable') || 'No shortcuts available for this page'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Navigation (scrollable area) */}
      <div className="flex-1 min-h-0 overflow-y-auto p-2">
        <nav className="space-y-1">
          <Link 
            to="/"
            className={cn(
              "flex w-full items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-accent/50 transition-colors",
              isHomeActive && "ring-2 ring-blue-400"
            )}
          >
            <House size={16} />
            {!isCollapsed && <span>{t('home')}</span>}
          </Link>

          {/* Analisi (non-archived) */}
          <div
            className={cn(
              "flex items-center gap-1 rounded-md",
              isEntitiesActive && "ring-2 ring-blue-400"
            )}
          >
            <Link 
              to="/entities"
              className={cn(
                "flex flex-1 items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-accent/50 transition-colors"
              )}
            >
              <Folders size={16} />
              {!isCollapsed && <span>{t('entities')}</span>}
            </Link>
            {!isCollapsed && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                aria-label={isEntitiesOpen ? t('collapseEntities') : t('expandEntities')}
                onClick={() => setIsEntitiesOpen((v) => !v)}
              >
                {isEntitiesOpen ? <CaretDown size={12} /> : <CaretUp size={12} />}
              </Button>
            )}
          </div>

          {/* Non-archived entity links (Analisi submenu) */}
          {!isCollapsed && isEntitiesOpen && nonArchived.length > 0 && (
            <div className="mt-1 space-y-1 ml-6">
              {nonArchived.map((entity) => {
                const openCount = getOpenQuestionCount(entity.id, state)
                const isActive = location.pathname.startsWith(`/entities/${entity.id}`)
                return (
                  <Link
                    key={entity.id}
                    to={`/entities/${entity.id}`}
                    className={cn(
                      "flex w-full items-center justify-between px-3 py-1.5 text-sm rounded-md hover:bg-accent/50 transition-colors",
                      isActive && "ring-2 ring-blue-400"
                    )}
                  >
                    <span className="truncate">{entity.name}</span>
                    {verboseMode && openCount > 0 && (
                      <Badge variant="secondary" className="ml-1 text-xs px-1 py-0 h-4">
                        {openCount}
                      </Badge>
                    )}
                  </Link>
                )
              })}
            </div>
          )}

          {/* Archivio (archived) */}
          <div
            className={cn(
              "flex items-center gap-1 mt-1 rounded-md",
              isArchivioActive && "ring-2 ring-blue-400"
            )}
          >
            <Link 
              to="/archivio"
              className={cn(
                "flex flex-1 items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-accent/50 transition-colors"
              )}
            >
              <Archive size={16} />
              {!isCollapsed && <span>{t('archivio')}</span>}
            </Link>
            {!isCollapsed && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                aria-label={isArchivioOpen ? t('collapseEntities') : t('expandEntities')}
                onClick={() => setIsArchivioOpen((v) => !v)}
              >
                {isArchivioOpen ? <CaretDown size={12} /> : <CaretUp size={12} />}
              </Button>
            )}
          </div>

          {/* Archived entity links (Archivio submenu) */}
          {!isCollapsed && isArchivioOpen && archived.length > 0 && (
            <div className="mt-1 space-y-1 ml-6">
              {archived.map((entity) => {
                const isActive = location.pathname.startsWith(`/archivio/${entity.id}`)
                return (
                  <Link
                    key={entity.id}
                    to={`/archivio/${entity.id}`}
                    className={cn(
                      "flex w-full items-center justify-between px-3 py-1.5 text-sm rounded-md hover:bg-accent/50 transition-colors",
                      isActive && "ring-2 ring-blue-400"
                    )}
                  >
                    <span className="truncate">{entity.name}</span>
                  </Link>
                )
              })}
            </div>
          )}

          {/* Email Extractor removed */}

          {/* Tutorials */}
          <Link 
            to="/tutorials"
            className={cn(
              "flex w-full items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-accent/50 transition-colors mt-1",
              isTutorialsActive && "ring-2 ring-blue-400"
            )}
          >
            <BookOpen size={16} />
            {!isCollapsed && <span>{t('tutorials')}</span>}
          </Link>
        </nav>
      </div>

      {/* Data Management */}
      {!isCollapsed && (
        <div className="p-3 border-t space-y-3">
          <div>
            <Label className="text-xs font-medium text-muted-foreground">
              {t('dataManagement')}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t('dataManagementHelp')}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <Button variant="outline" size="sm" className="justify-start" onClick={handleResetDemoData}>
              <ArrowClockwise size={12} className="mr-2" />
              {t('resetDemoData')}
            </Button>
            <Button variant="destructive" size="sm" className="justify-start" onClick={handleDeleteAllData}>
              <Trash size={12} className="mr-2" />
              {t('dataDeleteAll')}
            </Button>
            <Button variant="outline" size="sm" className="justify-start" onClick={handleExportData}>
              <DownloadSimple size={12} className="mr-2" />
              {t('dataExport')}
            </Button>
            <Button variant="outline" size="sm" className="justify-start" onClick={handleImportClick}>
              <UploadSimple size={12} className="mr-2" />
              {t('dataImport')}
            </Button>
          </div>
        </div>
      )}

      <input
        ref={importInputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={handleImportFile}
      />
    </div>
  )
}